import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import {
    Alert,
    Button,
    Input,
    Modal,
    ModalContent,
    ModalFooter,
    RadioGroup,
    useNotifications,
} from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { DeclarePartnerListDocument, PartnerListsDocument } from '../../graphql/__generated__/types';
import EligiblePartnerPicker, { PickedPartner } from './EligiblePartnerPicker';
import { partnerListRefusalOf } from './refusals';

interface Props {
    onClose: () => void;
}

type Display = 'nominative' | 'anonymous';

/**
 * « Déclarer une liste de partenaires » (RG-PTN-01, 05, 06, 10).
 *
 * **THE « AUTOMATIQUE » FEED IS RENDERED, DISABLED, WITH ITS REASON.** The mockup offers
 * it and even pre-selects it; the decision of 21/08 makes it post-MVP, and the API takes
 * no feed argument at all. Removing the tile would have been the other honest option, and
 * it is the wrong one here: this is the `accountModules` case, where four undelivered
 * modules out of six stay in the list, disabled, because a shortened list reads as an
 * incomplete referential and because an operator who has seen the mockup will look for
 * the entry. The rule is said once, under the field, never per tile.
 *
 * **The display mode has no pre-selected tile**, unlike the mockup, and that is deliberate:
 * `isAnonymous` decides what every client of this list will see, and a default would
 * choose it in the operator's place. The button stays disabled until they pick.
 *
 * **The theme rule is written on the screen** because nothing enforces it: the catalogue is
 * global, so a name is shared by every client and must name none of them. No constraint
 * can hold that, which is exactly why the sentence has to be here.
 */
const DeclarePartnerListDialog = ({ onClose }: Props) => {
    const { createNotification } = useNotifications();
    const [name, setName] = useState('');
    const [display, setDisplay] = useState<Display | null>(null);
    const [picked, setPicked] = useState<PickedPartner[]>([]);

    const [declare, { loading, error }] = useMutation(DeclarePartnerListDocument, {
        refetchQueries: [PartnerListsDocument],
        awaitRefetchQueries: true,
    });

    const trimmed = name.trim();
    // Three conditions, and the mockup only holds the first: a list is never declared
    // empty (the server refuses too), and the display mode is a decision about what
    // clients see rather than a default.
    const canSend = trimmed.length > 0 && display !== null && picked.length > 0;

    const submit = async () => {
        if (!canSend) {
            return;
        }
        try {
            await declare({
                variables: {
                    name: trimmed,
                    isAnonymous: display === 'anonymous',
                    sourcePartnerIds: picked.map((partner) => partner.sourcePartnerId),
                },
            });
            createNotification({
                type: 'success',
                text: c('Partner lists').t`List « ${trimmed} » declared`,
            });
            onClose();
        } catch {
            // The refusal is rendered from `error` below. Swallowed here so the modal
            // stays open on what the operator typed rather than closing on a failure.
        }
    };

    return (
        <Modal open onClose={onClose} title={c('Partner lists').t`Declare a partner list`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="partner-list-name" className="text-sm font-medium">
                            {c('Partner lists').t`List name`}
                        </label>
                        <Input
                            id="partner-list-name"
                            value={name}
                            onValue={setName}
                            placeholder="Partenaires IA"
                            aria-describedby="partner-list-name-rule"
                        />
                        <p id="partner-list-name-rule" className="text-xs text-text-600">
                            {c('Partner lists')
                                .t`The name is a theme, and the catalogue is shared by every client: it must never name one of them. It is unique across the whole platform, archived lists included, and archiving keeps it reserved for ever.`}
                        </p>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium">{c('Partner lists').t`Partner display`}</span>
                        <RadioGroup
                            category="card"
                            name="partner-list-display"
                            value={display ?? undefined}
                            onValue={(value: string) => setDisplay(value as Display)}
                            options={[
                                {
                                    value: 'nominative',
                                    label: c('Partner lists').t`Nominative`,
                                    description: 'Capgemini Invent',
                                },
                                {
                                    value: 'anonymous',
                                    label: c('Partner lists').t`Anonymous`,
                                    description: 'PTN-0427',
                                },
                            ]}
                        />
                        <p className="text-xs text-text-600">
                            {c('Partner lists')
                                .t`This is what the client sees, modal included. The back-office always sees the company name whatever you choose here, and the mode stays changeable from the list afterwards.`}
                        </p>
                    </div>

                    {/* Attention, CE N'EST PAS UN GROUPE RADIO, et la maquette en dessine un.
                        Le MVP ne compose qu'a la main (RG-PTN-06), l'API ne prend aucun
                        argument de mode, donc le groupe aurait porte une seule option
                        selectionnable et deja cochee : un controle que l'operateur actionne
                        pour rien, exactement ce que PCG-92 retire. Ce qui reste necessaire est
                        de NOMMER l'option absente : sans elle, qui compare avec la maquette
                        cherche une entree disparue sans explication. */}
                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium">{c('Partner lists').t`How the list is filled`}</span>
                        <p className="text-sm">{c('Partner lists')
                            .t`Manual: a selection among the eligible partners.`}</p>
                        <p className="text-xs text-text-600">
                            {c('Partner lists')
                                .t`Automatic feeding — every eligible partner, present and future — is not delivered, so there is nothing to choose here. How a list is filled is fixed at declaration and cannot be changed afterwards.`}
                        </p>
                    </div>

                    <EligiblePartnerPicker
                        picked={picked}
                        onPicked={setPicked}
                        searchPlaceholder={c('Partner lists').t`Search a partner`}
                        emptyTitle={c('Partner lists').t`No partner is eligible`}
                        emptyDescription={c('Partner lists')
                            .t`Eligibility is recomputed at every read from the Salesforce mirror. An empty referential means the mirror answered nothing, not that the network is empty: tell the support desk rather than declaring an empty list.`}
                    />

                    {trimmed.length > 0 && display !== null && picked.length === 0 && (
                        <Alert type="info">
                            {c('Partner lists').t`A list is never declared empty: pick at least one partner above.`}
                        </Alert>
                    )}

                    {error && <RefusalNotice refusal={partnerListRefusalOf(error)} />}
                </div>
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Partner lists').t`Cancel`}
                </Button>
                <Button onClick={submit} disabled={!canSend} loading={loading}>
                    {c('Partner lists').t`Declare the list`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default DeclarePartnerListDialog;
